import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

/**
 * Massachusetts Professional License Renewal Landing Page
 */

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const MALicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 24,
      cycleYears: 2,
      terminology: 'LU',
      highlights: ['24 LU biennial', 'Min 12 LU in Health, Safety & Welfare', '2 LU in Accessibility/Barrier-Free Design', 'Max 12 LU self-study', 'Reportable by October 31 renewal date'],
      stateBoard: 'Massachusetts Board of Registration of Architects',
      boardUrl: 'https://www.mass.gov/orgs/board-of-registration-of-architects'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 24,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['24 PDH biennial', '2 PDH in Ethics (mandatory)', 'General engineering subjects', 'Min 12 PDH per year', 'Max 12 PDH self-study per biennium'],
      stateBoard: 'Massachusetts Board of Registration of Professional Engineers & Land Surveyors',
      boardUrl: 'https://www.mass.gov/orgs/board-of-registration-of-professional-engineers-and-professional-land-surveyors'
    },
    {
      id: 'PLS',
      name: 'Professional Land Surveyor',
      icon: '📐',
      credits: 24,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['24 PDH biennial', '2 PDH in Ethics (mandatory)', 'Land surveying topics', 'Min 12 PDH per year', 'Professional development activities'],
      stateBoard: 'Massachusetts Board of Registration of Professional Engineers & Land Surveyors',
      boardUrl: 'https://www.mass.gov/orgs/board-of-registration-of-professional-engineers-and-professional-land-surveyors'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 80,
      cycleYears: 2,
      terminology: 'CPE',
      highlights: ['80 CPE hours biennial', 'Min 20 hours annually', '4 hours Ethics (every 2 years)', 'Min 40% technical subjects', 'Max 20 hours self-study per year'],
      stateBoard: 'Massachusetts Board of Public Accountancy',
      boardUrl: 'https://www.mass.gov/orgs/board-of-public-accountancy'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 15,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['15 contact hours biennial', 'Nursing practice-related', 'Max 7.5 hours self-study', 'First renewal exempt if licensed <1 year', 'Approved CE providers'],
      stateBoard: 'Massachusetts Board of Registration in Nursing',
      boardUrl: 'https://www.mass.gov/orgs/board-of-registration-in-nursing'
    },
    {
      id: 'APRN',
      name: 'Advanced Practice Registered Nurse',
      icon: '🩺',
      credits: 15,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['15 contact hours biennial', 'Advanced practice nursing topics', 'Must maintain national certification', 'Max 7.5 hours self-study', 'Separate from RN CE'],
      stateBoard: 'Massachusetts Board of Registration in Nursing',
      boardUrl: 'https://www.mass.gov/orgs/board-of-registration-in-nursing'
    },
    {
      id: 'LICSW',
      name: 'Licensed Independent Clinical Social Worker',
      icon: '🧠',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Cultural Competency (one-time)', 'Clinical social work practice', 'Max 15 hours online/self-study', 'Evidence-based practice'],
      stateBoard: 'Massachusetts Board of Registration of Social Workers',
      boardUrl: 'https://www.mass.gov/orgs/board-of-registration-of-social-workers'
    },
    {
      id: 'LMHC',
      name: 'Licensed Mental Health Counselor',
      icon: '💭',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Cultural Competency (one-time)', 'Mental health counseling topics', 'Max 15 hours online/self-study', 'Clinical practice focus'],
      stateBoard: 'Massachusetts Board of Registration of Allied Mental Health & Human Services Professionals',
      boardUrl: 'https://www.mass.gov/orgs/board-of-registration-of-allied-mental-health-human-services-professions'
    },
    {
      id: 'LMFT',
      name: 'Licensed Marriage & Family Therapist',
      icon: '👨‍👩‍👧‍👦',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '3 hours Cultural Competency (one-time)', 'Marriage and family therapy topics', 'Max 15 hours online/self-study', 'Systems theory approaches'],
      stateBoard: 'Massachusetts Board of Registration of Allied Mental Health & Human Services Professionals',
      boardUrl: 'https://www.mass.gov/orgs/board-of-registration-of-allied-mental-health-human-services-professions'
    },
    {
      id: 'RE',
      name: 'Real Estate Broker/Salesperson',
      icon: '🏠',
      credits: 12,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['12 hours biennial', 'State-approved courses', 'Real estate practice topics', 'Completion required before renewal', 'Online courses accepted'],
      stateBoard: 'Massachusetts Board of Registration of Real Estate Brokers & Salespersons',
      boardUrl: 'https://www.mass.gov/orgs/board-of-registration-of-real-estate-brokers-and-salespersons'
    },
    {
      id: 'APPR',
      name: 'Certified Real Estate Appraiser',
      icon: '📊',
      credits: 28,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['28 hours biennial', '7-hour National USPAP Update (mandatory)', 'AQB-approved education', 'Appraisal practice-related', 'State-specific requirements'],
      stateBoard: 'Massachusetts Board of Real Estate Appraisers',
      boardUrl: 'https://www.mass.gov/orgs/board-of-real-estate-appraisers'
    },
    {
      id: 'ATT',
      name: 'Attorney',
      icon: '⚖️',
      credits: 12,
      cycleYears: 1,
      terminology: 'CLE',
      highlights: ['12 CLE hours annually', '2 hours Ethics/Professionalism (mandatory)', 'Accredited programs required', 'Calendar year basis (Jan 1 - Dec 31)', 'Max 6 hours self-study'],
      stateBoard: 'Massachusetts Board of Bar Overseers - MCLE',
      boardUrl: 'https://www.mass.gov/orgs/massachusetts-board-of-bar-overseers'
    }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Massachusetts Professional License Renewal Requirements",
      "description": "Complete guide to Massachusetts professional license renewal requirements for Architects, Engineers, CPAs, Nurses, Social Workers, and more.",
      "url": "https://cectrack.com/ma-license-renewal",
      "about": {
        "@type": "Thing",
        "name": "Professional License Renewal",
        "sameAs": [
          "https://www.mass.gov/",
          "https://www.mass.gov/topics/professional-licensure"
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "CEC Track",
        "url": "https://cectrack.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cectrack.com/og-image.png"
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "Massachusetts Professional Licenses",
        "numberOfItems": 12,
        "itemListElement": professions.map((prof, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": prof.name,
            "description": `${prof.name} license renewal requires ${prof.credits} ${prof.terminology} every ${prof.cycleYears} year(s)`,
            "provider": {
              "@type": "Organization",
              "name": prof.stateBoard,
              "url": prof.boardUrl
            }
          }
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://cectrack.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Massachusetts License Renewal",
            "item": "https://cectrack.com/ma-license-renewal"
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do I need to renew my Massachusetts professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CE requirements vary by profession in Massachusetts. Architects, Engineers, and Surveyors need 24 hours biennial; CPAs need 80 CPE hours biennial; RNs and APRNs need 15 contact hours biennial; Social Workers and Mental Health professionals need 30 hours biennial; Real Estate professionals need 12 hours biennial; Appraisers need 28 hours biennial; Attorneys need 12 CLE hours annually."
          }
        },
        {
          "@type": "Question",
          "name": "Which state agency handles professional license renewal in Massachusetts?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The Massachusetts Division of Professional Licensure oversees most professional licensing boards including Architects, Engineers, Surveyors, CPAs, Nurses, Social Workers, Mental Health professionals, Real Estate professionals, and Appraisers. The Board of Bar Overseers administers attorney MCLE requirements separately."
          }
        },
        {
          "@type": "Question",
          "name": "What are the mandatory CE topics for Massachusetts professionals?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Massachusetts has several mandatory CE requirements: Architects need 12 LU in HSW and 2 LU in Accessibility; Engineers and Surveyors need 2 PDH in Ethics; CPAs need 4 hours Ethics every 2 years; Mental Health professionals need 3 hours Cultural Competency (one-time); Appraisers need 7-hour USPAP Update; Attorneys need 2 hours Ethics/Professionalism annually."
          }
        },
        {
          "@type": "Question",
          "name": "How often do I need to renew my Massachusetts professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most Massachusetts professions renew biennially (every 2 years). Attorneys renew annually with 12 CLE hours per year. Renewal dates vary by profession and often align with birth months or license expiration dates."
          }
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Massachusetts Division of Professional Licensure",
      "url": "https://www.mass.gov/orgs/division-of-professional-licensure",
      "telephone": "+1-617-701-8600",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "1000 Washington Street, Suite 710",
        "addressLocality": "Boston",
        "addressRegion": "MA",
        "postalCode": "02118",
        "addressCountry": "US"
      },
      "areaServed": "Massachusetts",
      "description": "Oversees professional licensing for Architects, Engineers, CPAs, Nurses, Social Workers, Mental Health professionals, and Real Estate professionals in Massachusetts"
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Massachusetts Board of Bar Overseers",
      "url": "https://www.mass.gov/orgs/massachusetts-board-of-bar-overseers",
      "telephone": "+1-617-728-8700",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "99 High Street, Boston Bar Association Tower",
        "addressLocality": "Boston",
        "addressRegion": "MA",
        "postalCode": "02110",
        "addressCountry": "US"
      },
      "areaServed": "Massachusetts",
      "description": "Administers Minimum Continuing Legal Education (MCLE) requirements for attorneys licensed in Massachusetts"
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Massachusetts Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to Massachusetts professional license renewal requirements. Track CE credits for Architects, Engineers, CPAs, Nurses, Social Workers, Attorneys, and more."
        keywords="Massachusetts license renewal, MA CE requirements, MA continuing education, Massachusetts professional licenses, MA DPL, Massachusetts nurses, MA CPAs, MA engineers, MA attorneys"
        canonical="https://cectrack.com/ma-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section 
        aria-label="Page header"
        style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                Massachusetts Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Never miss a deadline. Track your continuing education requirements across all your Massachusetts professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link 
                  to="/register" 
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                >
                  Start Tracking Free
                </Link>
                <a 
                  href="#professions" 
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }} aria-label="12 professions">12</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }} aria-label="Over 50 categories">50+</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }} aria-label="100 percent compliant">100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>DPL Compliant</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }} aria-label="24/7 access">24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professions Grid */}
      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
                Massachusetts CE Requirements by Profession
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Select your profession to see detailed continuing education requirements
              </p>
            </div>

          <div className="row g-4">
            {professions.map((prof) => (
              <div key={prof.id} className="col-md-6 col-lg-4">
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                    <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>
                      {prof.name}
                    </h5>
                    
                    <div 
                      style={{
                        backgroundColor: '#dbeafe',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e40af' }}>
                        {prof.credits} {prof.terminology}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        {prof.cycleYears === 1 ? 'Annual' : 'Biennial'}
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Key Requirements:
                      </div>
                      <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                        {prof.highlights.map((highlight, idx) => (
                          <li key={idx} style={{ marginBottom: '0.3rem' }}>{highlight}</li>
                        ))}
                      </ul>
                    </div>

                    <div 
                      style={{
                        paddingTop: '1rem',
                        borderTop: '1px solid #e9ecef',
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}
                    >
                      <strong>{prof.stateBoard}</strong>
                      <br />
                      <a 
                        href={prof.boardUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#3b82f6', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Visit State Board →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CEC Track */}
      <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                Simplify Your Massachusetts License Management
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                Managing multiple Massachusetts professional licenses doesn't have to be complicated. 
                CEC Track automatically tracks your requirements, calculates your progress, and sends 
                timely reminders so you never miss a renewal deadline.
              </p>
              <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                {[
                  'Automated deadline tracking for all MA professions',
                  'Upload certificates and match to mandatory topics',
                  'Real-time progress reports by category',
                  'Multi-state support for professionals licensed in multiple states',
                  'Secure cloud storage for all documents',
                  'Email notifications for upcoming deadlines'
                ].map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#3b82f6', fontSize: '1.5rem' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div 
                className="p-5 text-center"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>
                  Start Tracking Today
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                  Join hundreds of Massachusetts professionals who trust CEC Track to manage their continuing education.
                </p>
                <Link 
                  to="/register" 
                  className="btn btn-primary btn-lg px-5 py-3"
                  style={{ 
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Resources */}
      <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Massachusetts State Resources
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Official resources and contact information for Massachusetts professional licensing
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Massachusetts Division of Professional Licensure
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Oversees professional licensing boards for Architects, Engineers, Surveyors, CPAs, Nurses, Social Workers, Mental Health professionals, and Real Estate professionals.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+16177018600" aria-label="Call DPL at 617-701-8600">(617) 701-8600</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.mass.gov/orgs/division-of-professional-licensure" target="_blank" rel="noopener noreferrer" aria-label="Visit DPL website">
                      mass.gov/dpl
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 1000 Washington Street, Suite 710, Boston, MA 02118
                  </div>
                  <a 
                    href="https://www.mass.gov/topics/professional-licensure" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View all professional licensing boards"
                  >
                    View All Licensing Boards
                  </a>
                </div>
              </article>
            </div>

            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Massachusetts Board of Bar Overseers
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Administers Minimum Continuing Legal Education (MCLE) requirements for attorneys licensed in Massachusetts. Mandatory annual CLE includes Ethics and Professionalism.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+16177288700" aria-label="Call BBO at 617-728-8700">(617) 728-8700</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.mass.gov/orgs/massachusetts-board-of-bar-overseers" target="_blank" rel="noopener noreferrer" aria-label="Visit BBO website">
                      mass.gov/bbo
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 99 High Street, Boston Bar Association Tower, Boston, MA 02110
                  </div>
                  <a 
                    href="https://www.mass.gov/topics/continuing-legal-education-cle" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View MCLE requirements"
                  >
                    View MCLE Requirements
                  </a>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Other States Cross-Linking */}
      <OtherStates currentState="MA" />

      {/* CTA Section */}
      <section 
        aria-label="Call to action"
        style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: 'white',
          padding: '80px 0'
        }}
      >
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to Simplify Your Massachusetts License Renewal?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join professionals across Massachusetts who never worry about missing CE requirements
          </p>
          <Link 
            to="/register" 
            className="btn btn-light btn-lg px-5 py-3"
            style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}
          >
            Get Started Free
          </Link>
        </div>
      </section>
      </main>
    </>
  );
};

export default MALicenseRenewal;
